import React, { useState } from "react";
import { Tabs, Tab, Box, Tooltip } from "@mui/material";
import { TabContext } from "@mui/lab";
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DomainIcon from '@mui/icons-material/Domain';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

// Import your page components
import InvoicePage from "./proforma/InvoicePage";
import CompanyDetails from "./proforma/CompanyDetails";
import ProductService from "./proforma/ProductService";
import { styled } from '@mui/system';

// --- Main Tabs Configuration ---
// This array defines the tabs that will be displayed as the main navigation.
const mainTabs = [
    {
        label: "Proforma Invoice",
        value: "invoice",
        icon: <RequestQuoteIcon fontSize="large" />,
    },
    {
        label: "Company Details",
        value: "company",
        icon: <DomainIcon fontSize="large" />,
    },
    {
        label: "Product/Service Details",
        value: "product",
        icon: <Inventory2OutlinedIcon fontSize="large" />,
    },
];

// --- Custom Tooltip Styling ---
// This component is used to style the tooltips that appear on hover.
const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
    '& .MuiTooltip-tooltip': {
        backgroundColor: 'transparent',
        color: 'black',
        fontSize: 10,
        padding: 0,
        margin: 0,
    },
    '& .MuiTooltip-arrow': {
        display: 'none',
    },
}));

// --- Main Component ---
const InvoiceTabWrapper = () => {
    // --- SIMPLIFIED STATE ---
    // We only need one state to track the currently active tab.
    // The default tab is "invoice".
    const [currentTab, setCurrentTab] = useState("invoice");

    // --- SIMPLIFIED EVENT HANDLER ---
    // This function updates the state when a tab is clicked.
    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    // --- CONTENT RENDERER ---
    // This function renders the correct component based on the active tab.
    const renderTabContent = () => {
        switch (currentTab) {
            case "invoice":
                return <InvoicePage />;
            case "company":
                return <CompanyDetails />;
            case "product":
                return <ProductService />;
            default:
                return null; // Or a default component like <div>Select a tab</div>
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <TabContext value={currentTab}>
                {/* ==== MAIN TABS ==== */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        sx={{
                            backgroundColor: "#faf0e0",
                            borderRadius: "14px",
                            px: 2,
                            boxShadow: "0 0 5px #fb7102",
                            border: "1px solid #fb7102",
                            "& .MuiTabs-flexContainer": {
                                gap: "12px", // Adds space between the tabs
                            },
                            "& .MuiTab-root": {
                                width: 46,
                                height: 46,
                                borderRadius: "10px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                textTransform: "none",
                                minWidth: 'auto', // Important for icon-only tabs
                            },
                            "& .Mui-selected": {
                                backgroundColor: "#faf0e0",
                                color: "#fb7102",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            },
                            "& .MuiTabs-indicator": {
                                display: 'none', // Hide the default underline indicator
                            },
                        }}
                    >
                        {mainTabs.map((tab) => (
                            <Tab
                                key={tab.value}
                                value={tab.value}
                                icon={
                                    <CustomTooltip title={tab.label} arrow>
                                        {tab.icon}
                                    </CustomTooltip>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>

                {/* ==== TAB CONTENT AREA ==== */}
                <Box>
                    {renderTabContent()}
                </Box>
            </TabContext>
        </Box>
    );
};

export default InvoiceTabWrapper;